/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {NgModel} from '@angular/forms';
import {Subscription} from 'rxjs';

@Component({
  standalone: false,
  selector: 'adn-admin-field-validation',
  template: `
    <div class="help-block" *ngIf="showRequired">
      {{ 'validation.required' | ogTranslate }}
    </div>
    <div class="help-block" *ngIf="showMin">
      {{ minMessage }}
    </div>
    <div class="help-block" *ngFor="let apiError of apiErrors; trackBy: trackByIndex">
      {{ apiError }}
    </div>
  `
})
export class AdminFieldValidationComponent implements OnChanges, OnDestroy {
  private static readonly EMPTY_API_ERRORS: string[] = [];

  @Input() ngModelRef: NgModel;
  @Input() parameters: Record<string, any> = {};

  showRequired = false;
  showMin = false;
  minMessage = '';
  apiErrors: string[] = AdminFieldValidationComponent.EMPTY_API_ERRORS;

  private subscription: Subscription;

  constructor(
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
  }

  ngOnChanges(): void {
    this.subscription?.unsubscribe();
    this.sync();
    if (this.ngModelRef) {
      this.subscription = this.ngModelRef.statusChanges.subscribe(() => {
        this.ngZone.run(() => this.sync());
      });
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  trackByIndex(index: number): number {
    return index;
  }

  private sync(): void {
    const control = this.ngModelRef?.control;
    const showRequired = !!control?.errors?.required;
    const showMin = !!control?.errors?.min;
    const minMessage = showMin
      ? this.translate.instant('validation.min', this.parameters)
      : '';
    const apiErrors = control?.errors
      ? Object.keys(control.errors)
        .filter((key) => key !== 'required' && key !== 'min' && key !== 'validation')
        .map((key) => control.errors[key]?.apiText)
        .filter(Boolean)
      : AdminFieldValidationComponent.EMPTY_API_ERRORS;
    const apiErrorsUnchanged = apiErrors.length === this.apiErrors.length
      && apiErrors.every((error, index) => error === this.apiErrors[index]);
    if (showRequired === this.showRequired
      && showMin === this.showMin
      && minMessage === this.minMessage
      && apiErrorsUnchanged) {
      return;
    }
    this.showRequired = showRequired;
    this.showMin = showMin;
    this.minMessage = minMessage;
    this.apiErrors = apiErrors;
    this.cdr.markForCheck();
  }
}
